import {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import * as authApi from '../api/auth';
import * as passkeyApi from '../api/passkeys';
import { ApiError, SESSION_EXPIRED_EVENT } from '../api/client';
import { credentialToJSON, parseRequestOptionsFromJSON } from '../utils/webauthn';
import {
  AccountAccess,
  AccountRole,
  Credentials,
  CurrentUser,
  MembershipStatus,
  ProfileUpdate,
  Registration,
} from '../types/auth';
import { claimForUser } from '../offline/db';
import { startSync } from '../offline/sync';

interface AuthState {
  /** Null once checked and nobody is signed in; undefined while still checking. */
  user: CurrentUser | null;
  /** True until the initial session probe finishes, so guards can avoid flashing. */
  loading: boolean;

  /**
   * The account new games are recorded into. Defaults to the user's own account.
   * A contributor on someone else's account switches to it to record there.
   */
  activeAccountId: string | null;
  activeAccount: AccountAccess | null;
  setActiveAccountId: (accountId: string) => void;

  /** True when the user may record into the active account. */
  canWrite: boolean;
  /** True when the user may manage members of the active account. */
  canAdminister: boolean;

  /**
   * True when the current session could not be confirmed with the server and the
   * user shown is a cached one from the last time it was. A facility can go days
   * without connectivity, and that must not sign anyone out.
   */
  offlineSession: boolean;

  signIn: (credentials: Credentials) => Promise<void>;
  signUp: (registration: Registration) => Promise<void>;
  /** Usernameless passkey sign-in: the authenticator (Face ID, a security key, ...)
   * picks the account, so there is nothing to pass in. */
  signInWithPasskey: () => Promise<void>;
  signOut: () => Promise<void>;
  /** Re-reads the user, for after membership changes. */
  refresh: () => Promise<void>;
  /** Edits the signed-in person's own display name, email, and optionally password. */
  updateProfile: (update: ProfileUpdate) => Promise<void>;
  /**
   * Starts a new operational account owned by the user and switches to it. Requires
   * already holding an active Organizer/Head Coach role (see `selectRole`) — free and
   * instant otherwise, since that role is what was paid for, not this.
   */
  startAccount: (name: string) => Promise<AccountAccess>;
  /**
   * Picks (or re-attempts payment for) the signed-in person's own platform-wide role.
   * If activation is free (the default today), it takes effect immediately and this
   * refreshes the user; the caller checks the returned `status`/`checkoutUrl` to know
   * whether to redirect to Stripe instead.
   */
  selectRole: (role: AccountRole) => Promise<{ status: MembershipStatus; checkoutUrl: string | null }>;
}

const AuthContext = createContext<AuthState | undefined>(undefined);

const ACTIVE_ACCOUNT_KEY = 'curlingcodex.activeAccountId';

/**
 * The last session the server itself confirmed, kept outside IndexedDB (which is
 * partitioned per user, see offline/db.ts) so it can be read before anyone is known
 * to be signed in at all — the one piece of bootstrapping that has to work before
 * the offline mirror can even be opened.
 */
const CACHED_SESSION_KEY = 'curlingcodex.cachedSession';

function readCachedSession(): CurrentUser | null {
  try {
    const raw = localStorage.getItem(CACHED_SESSION_KEY);
    return raw ? (JSON.parse(raw) as CurrentUser) : null;
  } catch {
    return null;
  }
}

function writeCachedSession(user: CurrentUser | null): void {
  try {
    if (user) {
      localStorage.setItem(CACHED_SESSION_KEY, JSON.stringify(user));
    } else {
      localStorage.removeItem(CACHED_SESSION_KEY);
    }
  } catch {
    // Best-effort: a full storage quota should not block signing in or out.
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<CurrentUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeAccountId, setActiveAccountIdState] = useState<string | null>(null);
  const [offlineSession, setOfflineSession] = useState(false);

  /** Picks the account to act on: the last chosen one if still valid, else the default. */
  const resolveActiveAccount = useCallback((next: CurrentUser | null): string | null => {
    if (!next) return null;

    const remembered = localStorage.getItem(ACTIVE_ACCOUNT_KEY);
    if (remembered && next.accounts.some(a => a.accountId === remembered)) {
      return remembered;
    }
    return next.defaultWriteAccountId ?? next.accounts[0]?.accountId ?? null;
  }, []);

  /**
   * Points the offline mirror at the right database. Called as a plain function
   * from wherever the user is set, never from its own effect: React runs a child
   * component's effects before its parent's, so a page's own data-fetching effect
   * could otherwise run before this one and find no database claimed yet. Calling it
   * inline guarantees the mirror is pointed correctly before React even renders the
   * change, let alone mounts a page that reads from it.
   */
  const claimDatabase = useCallback((next: CurrentUser | null) => {
    if (next) claimForUser(next.id);
  }, []);

  /** Applies a session the server itself just confirmed — the normal path. */
  const applyUser = useCallback(
    (next: CurrentUser | null) => {
      claimDatabase(next);
      setUser(next);
      setActiveAccountIdState(resolveActiveAccount(next));
      setOfflineSession(false);
      writeCachedSession(next);
    },
    [resolveActiveAccount, claimDatabase]
  );

  useEffect(() => {
    let cancelled = false;

    authApi
      .fetchCurrentUser()
      .then(next => {
        if (!cancelled) applyUser(next);
      })
      .catch(error => {
        // Unreachable, not unauthenticated: a facility can go days without
        // connectivity, and that must not sign out someone with a session the
        // server would still honor if it could be asked. Fall back to the last
        // session it did confirm rather than bouncing them to sign-in.
        if (error instanceof ApiError && error.status === 0) {
          const cached = readCachedSession();
          if (!cancelled && cached) {
            claimDatabase(cached);
            setUser(cached);
            setActiveAccountIdState(resolveActiveAccount(cached));
            setOfflineSession(true);
            return;
          }
        }
        console.error('Could not check the current session:', error);
        if (!cancelled) applyUser(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [applyUser, resolveActiveAccount, claimDatabase]);

  // Drop the cached user if any request reports the session has gone, so the guards
  // send the user back to sign-in rather than leaving a dead screen up.
  useEffect(() => {
    const onExpired = () => applyUser(null);
    window.addEventListener(SESSION_EXPIRED_EVENT, onExpired);
    return () => window.removeEventListener(SESSION_EXPIRED_EVENT, onExpired);
  }, [applyUser]);

  // Points the offline mirror at this user's own database (never anyone else's —
  // see offline/db.ts) and, when reachable, drains queued writes and refreshes every
  // mirrored resource from what the server currently returns for this user's live
  // account access. That refresh is also how a record from an account they've since
  // lost access to gets purged, since pull() deletes anything local the server no
  // longer lists. None of this requires connectivity to have any effect on its
  // own — claimForUser only ever switches which database is active, so a person's
  // queued offline work is exactly as they left it whether or not this sync succeeds.
  useEffect(() => {
    if (!user) return;

    claimForUser(user.id);
    const stopSync = startSync();
    return stopSync;
  }, [user?.id]);

  const setActiveAccountId = useCallback((accountId: string) => {
    localStorage.setItem(ACTIVE_ACCOUNT_KEY, accountId);
    setActiveAccountIdState(accountId);
  }, []);

  const signIn = useCallback(
    async (credentials: Credentials) => {
      applyUser(await authApi.login(credentials));
    },
    [applyUser]
  );

  const signUp = useCallback(
    async (registration: Registration) => {
      applyUser(await authApi.register(registration));
    },
    [applyUser]
  );

  const signInWithPasskey = useCallback(async () => {
    const optionsJson = await passkeyApi.passkeyLoginOptions();
    const options = parseRequestOptionsFromJSON(JSON.parse(optionsJson).publicKey);
    const credential = (await navigator.credentials.get({
      publicKey: options,
    })) as PublicKeyCredential;
    applyUser(await passkeyApi.finishPasskeyLogin(JSON.stringify(credentialToJSON(credential))));
  }, [applyUser]);

  const signOut = useCallback(async () => {
    try {
      await authApi.logout();
    } finally {
      // Clear locally even if the call failed, so the user is not stuck signed in.
      localStorage.removeItem(ACTIVE_ACCOUNT_KEY);
      applyUser(null);
    }
  }, [applyUser]);

  const refresh = useCallback(async () => {
    applyUser(await authApi.fetchCurrentUser());
  }, [applyUser]);

  const updateProfile = useCallback(
    async (update: ProfileUpdate) => {
      applyUser(await authApi.updateProfile(update));
    },
    [applyUser]
  );

  const startAccount = useCallback(
    async (name: string) => {
      const account = await authApi.createAccount(name);
      await refresh();
      // The new account is what the person just asked to switch to, regardless of
      // whatever refresh's own default-picking logic would otherwise have chosen.
      setActiveAccountId(account.accountId);
      return account;
    },
    [refresh, setActiveAccountId]
  );

  const selectRole = useCallback(
    async (role: AccountRole) => {
      const result = await authApi.selectRole(role);
      if (result.status === 'active') {
        await refresh();
      }
      return result;
    },
    [refresh]
  );

  const value = useMemo<AuthState>(() => {
    const activeAccount =
      user?.accounts.find(a => a.accountId === activeAccountId) ?? null;

    return {
      user,
      loading,
      activeAccountId,
      activeAccount,
      setActiveAccountId,
      canWrite: activeAccount?.canWrite ?? false,
      canAdminister: activeAccount?.canAdminister ?? false,
      offlineSession,
      signIn,
      signUp,
      signInWithPasskey,
      signOut,
      refresh,
      updateProfile,
      startAccount,
      selectRole,
    };
  }, [
    user,
    loading,
    activeAccountId,
    setActiveAccountId,
    offlineSession,
    signIn,
    signUp,
    signInWithPasskey,
    signOut,
    refresh,
    updateProfile,
    startAccount,
    selectRole,
  ]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used inside an AuthProvider');
  }
  return context;
}
